import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  BedDouble,
  Bath,
  Ruler,
  Home,
  Phone,
  ZoomIn,
  X,
  CheckCircle2,
} from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import WhatsAppIcon from "../components/site/WhatsAppIcon";
import { fetchProperty, formatPrice } from "../lib/api";
import { OFFICE, INMOBILIARIA_WHATSAPP, typeLabel } from "../data/content";

const PropertyDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setZoom(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = zoom ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [zoom]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    fetchProperty(id)
      .then((data) => {
        setProperty(data);
        document.title = `${data.title} · Inmobiliaria Rendón Ramos`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const photos = property?.photos || [];
  const waLink =
    INMOBILIARIA_WHATSAPP.whatsapp.split("?")[0] +
    "?text=" +
    encodeURIComponent(
      `Hola, me interesa la propiedad "${property?.title || ""}" que vi en su página. ¿Me puede dar más información?`
    );

  return (
    <div className="detail-page">
      <Header />
      <div className="rr-container detail-wrap">
        <Link to="/#inmobiliaria" className="back-link" data-testid="back-to-properties">
          <ArrowLeft size={16} /> Volver a propiedades
        </Link>

        {loading ? (
          <div className="detail-grid">
            <div>
              <div className="skeleton-card">
                <div className="skeleton-media" />
              </div>
            </div>
            <div className="skeleton-card" style={{ padding: 10 }}>
              <div className="skeleton-line" style={{ width: "46%" }} />
              <div className="skeleton-line" style={{ width: "72%" }} />
              <div className="skeleton-line" style={{ width: "58%" }} />
            </div>
          </div>
        ) : notFound ? (
          <div className="empty-state" data-testid="property-not-found">
            <h4 className="font-display">Propiedad no encontrada</h4>
            <p>Es posible que esta propiedad ya no esté disponible.</p>
            <Link to="/#inmobiliaria" className="btn btn-orange" style={{ marginTop: 16 }}>
              Ver otras propiedades
            </Link>
          </div>
        ) : (
          <>
            <div className="detail-grid">
              <div>
                <button
                  type="button"
                  className="gallery-main"
                  onClick={() => photos.length > 0 && setZoom(true)}
                  aria-label="Ampliar foto"
                  data-testid="gallery-main"
                >
                  {photos.length > 0 ? (
                    <>
                      <img src={photos[activePhoto]} alt={property.title} />
                      <span className="gallery-zoom-hint">
                        <ZoomIn size={15} /> Toca para acercar
                      </span>
                    </>
                  ) : (
                    <div className="placeholder-icon" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#a9b9dd" }}>
                      <Home size={80} strokeWidth={1} />
                    </div>
                  )}
                </button>
                {photos.length > 1 && (
                  <div className="gallery-thumbs" data-testid="gallery-thumbs">
                    {photos.map((p, i) => (
                      <button
                        key={i}
                        className={`gallery-thumb ${i === activePhoto ? "active" : ""}`}
                        onClick={() => setActivePhoto(i)}
                        data-testid={`gallery-thumb-${i}`}
                      >
                        <img src={p} alt={`Foto ${i + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
                <div className="detail-desc">
                  <h4 className="font-display">Descripción</h4>
                  <p data-testid="property-description">
                    {property.description || "Contáctenos para más información sobre esta propiedad."}
                  </p>
                </div>
              </div>

              <aside className="detail-sidebar">
                <span className={`prop-badge ${property.operation === "renta" ? "renta" : ""}`} style={{ position: "static", display: "inline-block" }}>
                  {property.operation === "renta" ? "Renta" : "Venta"}
                </span>
                {property.status !== "disponible" && (
                  <span className={`status-chip status-${property.status}`} style={{ marginLeft: 8 }}>
                    {property.status === "vendido" ? "Vendida" : "Rentada"}
                  </span>
                )}
                <div className="detail-price" data-testid="detail-price" style={{ marginTop: 14 }}>
                  {formatPrice(property.price, property.currency)}
                  {property.operation === "renta" && Number(property.price) > 0 ? (
                    <span style={{ fontSize: 15, fontWeight: 600, color: "var(--rr-ink-soft)" }}> /mes</span>
                  ) : null}
                </div>
                <h1 className="detail-title font-display" data-testid="detail-title">
                  {property.title}
                </h1>
                {property.location ? (
                  <div className="property-location" style={{ marginTop: 10 }}>
                    <MapPin size={15} /> {property.location}
                  </div>
                ) : null}

                <div className="detail-specs">
                  <div className="spec-box">
                    <strong>{typeLabel(property.property_type)}</strong>
                    <span>Tipo</span>
                  </div>
                  {Number(property.bedrooms) > 0 && (
                    <div className="spec-box">
                      <strong>
                        <BedDouble size={15} style={{ display: "inline", marginRight: 4 }} />
                        {property.bedrooms}
                      </strong>
                      <span>Recámaras</span>
                    </div>
                  )}
                  {Number(property.bathrooms) > 0 && (
                    <div className="spec-box">
                      <strong>
                        <Bath size={15} style={{ display: "inline", marginRight: 4 }} />
                        {property.bathrooms}
                      </strong>
                      <span>Baños</span>
                    </div>
                  )}
                  {Number(property.area_m2) > 0 && (
                    <div className="spec-box">
                      <strong>{property.area_m2}</strong>
                      <span>m²</span>
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 24 }}>
                  {/* Features grid */}
                  {property.features && property.features.length > 0 && (
                    <div className="detail-features" data-testid="detail-features">
                      <h4 className="detail-features-title">Características</h4>
                      <ul className="detail-features-grid">
                        {property.features.map((f, i) => (
                          <li key={i}>
                            <CheckCircle2 size={15} strokeWidth={2} />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-whatsapp"
                    data-testid="detail-whatsapp-btn"
                  >
                    <WhatsAppIcon size={18} /> Me interesa · WhatsApp
                  </a>
                  <a href={OFFICE.phoneTel} className="btn btn-call" data-testid="detail-call-btn">
                    <Phone size={16} /> Llamar a oficina: {OFFICE.phoneDisplay}
                  </a>
                </div>
              </aside>
            </div>

            {zoom && photos.length > 0 && (
              <div
                className="photo-lightbox"
                onClick={() => setZoom(false)}
                role="dialog"
                aria-modal="true"
                data-testid="photo-lightbox"
              >
                <button
                  className="lightbox-close"
                  onClick={() => setZoom(false)}
                  aria-label="Cerrar"
                  data-testid="lightbox-close"
                >
                  <X size={22} />
                </button>
                <img
                  src={photos[activePhoto]}
                  alt={property.title}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PropertyDetail;
